import React, { useState } from 'react';
import flatten from 'lodash/flatten';

import ColorConstants from '../../../colors/ColorConstants';

import iconMap from './iconMap.json';

import WpIcon from '../WpIcon';
import { WpMenu, WpMenuItem } from '../../menu';
import { WpContainer, WpContainerBody, WpContainerHeader } from '../../container';
import WpBadge, { Color } from '../../badge/WpBadge';
import WpTextField from '../../text-field/WpTextField';
import { WpButton } from '../../button';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from '../../dropdown';
import { WpModal, WpModalBody, WpModalHeader } from '../../modal';
import { WpFormLayout } from '../../form';
import { WpDataTableV2 } from '../../table-v2';
import { WpRichTextInput } from '../../rich-text';
import WpAlert from '../../alert/WpAlert';

import './IconDocs.scss';

const IconsDocs = () => {
  const [activeTab, setActiveTab] = useState('guide');
  const baseClassName = 'IconDocs';

  const normalizeIcon = (prefix, name) => {
    return { prefix, name };
  };

  const icons = flatten(
    Object.keys(iconMap).map(prefix => {
      return iconMap[prefix].map(icon => normalizeIcon(prefix, icon));
    })
  );

  const Icon = ({ prefix, name }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSize, setActiveSize] = useState('lg');

    let badgeColor = 'default';
    switch (prefix) {
      case 'fas':
        badgeColor = 'orange';
        break;
      case 'fac':
        badgeColor = 'red';
        break;
      case 'fab':
        badgeColor = 'blue';
        break;
      default:
        badgeColor = 'gray';
        break;
    }

    return (
      <>
        <div onClick={() => setIsOpen(!isOpen)}>
          <WpContainer className={`${baseClassName}__icon`}>
            <WpContainerBody>
              <div className={`${baseClassName}__icon__body`}>
                <div className={`${baseClassName}__icon__badge`}>
                  <WpBadge color={badgeColor as Color}>{prefix}</WpBadge>
                </div>
                <div className={`${baseClassName}__icon__icon`}>
                  <WpIcon prefix={prefix} name={name} size="lg" />
                </div>
                <span className={`${baseClassName}__icon__name`}>{name}</span>
              </div>
            </WpContainerBody>
          </WpContainer>
        </div>
        {isOpen && (
          <WpModal isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} size="sm">
            <WpModalHeader>Icon</WpModalHeader>
            <WpModalBody>
              <div className={`${baseClassName}__icon__modal`}>
                <div className={`${baseClassName}__icon__modal__size`}>
                  <WpMenu direction="horizontal" activeItemId={activeSize} onMenuItemClick={setActiveSize}>
                    <WpMenuItem menuItemId="sm">small</WpMenuItem>
                    <WpMenuItem menuItemId="md">medium</WpMenuItem>
                    <WpMenuItem menuItemId="lg">large</WpMenuItem>
                  </WpMenu>
                </div>
                <div className={`${baseClassName}__icon__modal__icon`}>
                  <WpIcon prefix={prefix} name={name} size={activeSize} />
                </div>
                <h5 className={`${baseClassName}__icon__modal__label`}>{name}</h5>
                <WpFormLayout>
                  <WpTextField id="icon" label="Icon name" value={name} hideBorder enableCopy disabled />
                  <WpTextField id="prefix" label="Icon prefix" value={prefix} hideBorder enableCopy disabled />
                  <WpTextField
                    id="jsx"
                    label="JSX"
                    value={`<WpIcon ${prefix !== 'fal' ? `prefix="${prefix}"` : ''} name="${name}"  ${
                      activeSize !== 'md' ? `size="${activeSize}"` : ''
                    } />`}
                    hideBorder
                    enableCopy
                    disabled
                  />
                </WpFormLayout>
              </div>
            </WpModalBody>
          </WpModal>
        )}
      </>
    );
  };

  const Guide = () => {
    return (
      <div className={`${baseClassName}__guide`}>
        <h3>Examples</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ width: '45%' }}>
            <WpContainer>
              <WpContainerHeader>Basic Usage</WpContainerHeader>
              <WpContainerBody>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '150px' }}
                >
                  <WpIcon prefix="fal" name="home" />
                  <WpIcon prefix="fas" name="check-circle" />
                  <WpIcon prefix="fab" name="jira" />
                  <WpIcon prefix="fac" name="nested-link" />
                </div>
              </WpContainerBody>
              <WpContainerBody>
                <WpRichTextInput
                  value='<WpIcon prefix="fal" name="home" />&#10;<WpIcon prefix="fas" name="check-circle" />&#10;<WpIcon prefix="fab" name="jira" />&#10;WpIcon prefix="fac" name="nested-link" />'
                  disabled
                  options={{ showToolbar: false }}
                  height=""
                />
              </WpContainerBody>
            </WpContainer>
          </div>
          <div style={{ width: '45%' }}>
            <WpContainer>
              <WpContainerHeader>Size</WpContainerHeader>
              <WpContainerBody>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '100px' }}
                >
                  <WpIcon name="comment" size="sm" />
                  <WpIcon name="comment" size="md" />
                  <WpIcon name="comment" size="lg" />
                </div>
              </WpContainerBody>
              <WpContainerBody>
                <WpRichTextInput
                  value='<WpIcon name="comment" size="sm" />&#10;<WpIcon name="comment" size="md" />&#10;<WpIcon name="comment" size="lg" />'
                  disabled
                  options={{ showToolbar: false }}
                  height=""
                />
              </WpContainerBody>
            </WpContainer>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ width: '45%' }}>
            <WpContainer>
              <WpContainerHeader>Prefix</WpContainerHeader>
              <WpContainerBody>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '50px' }}
                >
                  <WpIcon prefix="fal" name="thumbtack" />
                  <WpIcon prefix="fas" name="thumbtack" />
                </div>
              </WpContainerBody>
              <WpContainerBody>
                <WpRichTextInput
                  value='<WpIcon prefix="fal" name="thumbtack"  />&#10;<WpIcon prefix="fas" name="thumbtack" />'
                  disabled
                  options={{ showToolbar: false }}
                  height=""
                />
              </WpContainerBody>
              <WpContainerBody>
                <WpAlert>
                  Each individual icon prefix and icon name combination needs to be imported into
                  `FontAwesomeLibrary.tsx` before it can be used in the app.
                </WpAlert>
              </WpContainerBody>
            </WpContainer>
          </div>
          <div style={{ width: '45%' }}>
            <WpContainer>
              <WpContainerHeader>Color</WpContainerHeader>
              <WpContainerBody>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '170px' }}
                >
                  <WpIcon name="home" color={ColorConstants.blue[500]} />
                  <WpIcon name="magic" color={ColorConstants.red[500]} />
                  <WpIcon name="history" color={ColorConstants.orange[500]} />
                  <WpIcon prefix="fas" name="check-circle" color={ColorConstants.green[500]} />
                </div>
              </WpContainerBody>
              <WpContainerBody>
                <WpRichTextInput
                  value='<WpIcon name="home" color={ColorConstants.blue[500]} />&#10;<WpIcon name="magic" color={ColorConstants.red[500]} />&#10;<WpIcon name="history" color={ColorConstants.orange[500]} />&#10;<WpIcon prefix="fas" name="check-circle" color={ColorConstants.green[500]} />'
                  disabled
                  options={{ showToolbar: false }}
                  height=""
                />
              </WpContainerBody>
              <WpContainerBody>
                <WpAlert>
                  When using the `color` prop, be sure to use the `ColorConstants` from the design system to ensure
                  colors are on brand.
                </WpAlert>
              </WpContainerBody>
            </WpContainer>
          </div>
        </div>
        <h3 style={{ marginTop: '2rem' }}>API</h3>
        <WpDataTableV2
          tableId="icons-prop-table"
          columns={[
            {
              label: 'Prop',
              selector: row => row.name,
              component: ({ row }) => {
                return <b>{row.name}</b>;
              },
              isSortable: false,
            },
            {
              label: 'Description',
              selector: row => row.description,
              isSortable: false,
            },
            {
              label: 'Type',
              selector: row => row.type,
              component: ({ row }) => {
                return (
                  <span>
                    {row.type && row.type}
                    {row?.typeOptions && typeof row?.typeOptions === 'object' && (
                      <>
                        {row.type && <>&nbsp;&nbsp;</>}
                        {row.typeOptions.map((option, index) => (
                          <React.Fragment key={option}>
                            <WpBadge>{option}</WpBadge>
                            {index < row.typeOptions.length - 1 && (
                              <span style={{ color: 'lightgray' }}>&nbsp;|&nbsp;</span>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </span>
                );
              },
              isSortable: false,
            },
            {
              label: 'Default',
              selector: row => row.default,
              component: ({ row }) => {
                if (row.default === '-') return row.default;
                return <WpBadge>{row.default}</WpBadge>;
              },
              isSortable: false,
            },
            {
              label: 'Required',
              selector: row => row.required,
              isSortable: false,
            },
          ]}
          data={[
            {
              name: 'name',
              description: 'name of icon to render (see icons list)',
              type: 'string',
              default: '-',
              required: 'true',
            },
            {
              name: 'prefix',
              description: 'the prefix of the icon to render',
              type: 'string',
              typeOptions: ['fal', 'fas', 'fab', 'fac'],
              default: 'fal',
              required: '-',
            },
            {
              name: 'size',
              description: 'size of icon to render',
              type: 'string',
              typeOptions: ['sm', 'md', 'lg'],
              default: 'md',
              required: '-',
            },
            {
              name: 'color',
              description: 'applies a color directly to the icon directly within JSX.',
              type: 'string',
              default: '-',
              required: '-',
            },
          ]}
        />
        <h3 style={{ marginTop: '2rem' }}>Notes</h3>
        <p>More details coming soon.</p>
        <p>TODO: @josh to add more details about adding and managing Icons for the app.</p>
      </div>
    );
  };

  const IconList = () => {
    const [search, setSearch] = useState('');
    const [prefixFilter, setPrefixFilter] = useState('all');

    let filteredIcons = icons;
    filteredIcons = filteredIcons
      .filter(i => i.name.includes(search.toLowerCase()))
      .filter(i => prefixFilter === 'all' || prefixFilter === i.prefix);

    return (
      <div className={`${baseClassName}__list-wrapper`}>
        <div className={`${baseClassName}__list-wrapper__toolbar`}>
          <div className={`${baseClassName}__list-wrapper__toolbar__search`}>
            <WpTextField
              id="icon-search"
              label="Search Icons"
              placeholder="Search icons"
              labelHidden
              onChange={setSearch}
              value={search}
              prefixIcon="search"
              onClear={search ? () => setSearch('') : null}
            />
          </div>
          <div className={`${baseClassName}__list-wrapper__toolbar__filter`}>
            <WpDropdown>
              <WpDropdownToggle>
                <WpButton variant="tertiary" size="lg">
                  {prefixFilter}
                  <WpIcon name="angle-down" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem onClick={() => setPrefixFilter('all')}>all</WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setPrefixFilter('fal')}>fal</WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setPrefixFilter('fas')}>fas</WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setPrefixFilter('fab')}>fab</WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setPrefixFilter('fac')}>fac</WpDropdownMenuItem>
              </WpDropdownMenu>
            </WpDropdown>
          </div>
        </div>
        <div className={`${baseClassName}__list-wrapper__list`}>
          {filteredIcons.map(icon => (
            <Icon prefix={icon.prefix} name={icon.name} key={`${icon.prefix}-${icon.name}`} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__tab-bar`}>
        <WpMenu direction="horizontal" onMenuItemClick={setActiveTab} activeItemId={activeTab}>
          <WpMenuItem menuItemId="guide">Guide</WpMenuItem>
          <WpMenuItem menuItemId="list">Icons</WpMenuItem>
        </WpMenu>
      </div>
      {activeTab === 'guide' && <Guide />}
      {activeTab === 'list' && <IconList />}
    </div>
  );
};

export default IconsDocs;
